'use strict';

import Postcode from './Postcode';

export default {
    billingAddress: {},
    shippingAddress: {},
    order: {},
    fetched: false,
    fetching: false,
    lines: [],
    shippingMethods: [],
    reference: null,
    notes: null,
    postcode: null,

    /**
     * Gets the order
     */
    getOrder() {
        if (!this.fetched) {
            this.fetched = true;
            this.refresh().then(response => {
                this.order = response;
            });
        }
        return this.order;
    },

    setPostcode(postcode) {
        this.postcode = postcode;
    },

    /**
     * Set the address on the order
     * @param {string} type
     * @param {object} payload
     * @param {boolean} offline
     */
    setAddress(type, payload, offline, addressId) {

        if (offline) {
            if (type == 'shipping') {
                this.shippingAddress = payload;
            } else {
                this.billingAddress = payload
            }
            return;
        }

        payload.zip = Postcode.format(payload.zip);
        var endpoint = 'delivery';

        if (type == 'billing') {
            endpoint = 'billing';
        }

        if (addressId) {
            Object.assign(payload, {
                address_id: addressId,
            });
        } else {
            delete payload.address_id;
        }

        return new Promise((resolve, reject) => {
            axios({
                method: 'PUT',
                url: '/api/orders/' + endpoint,
                headers: {
                    'Accept': 'application/json'
                },
                data: {
                    payload
                }
            }).then(response => {
                if (type == 'shipping') {
                    this.shippingAddress = payload;
                    this.updateShippingMethods();
                } else {
                    this.billingAddress = payload;
                }
                resolve(response);
            }).catch(errors => {
                reject(errors.response.data);
            });
        });
    },

    /**
     * Update the shipping methods
     */
    updateShippingMethods() {
        return new Promise((resolve, reject) => {
            axios({
                method: 'GET',
                url: '/api/orders/shipping/methods',
                headers: {
                    'Accept': 'application/json'
                }
            }).then(response => {
                resolve(response);
                this.shippingMethods = response.data;
            }).catch(errors => {
                reject(errors);
            });
        });
    },

    /**
     * Refresh the order from the API
     */
    refresh() {
        this.fetching = true;

        return new Promise((resolve, reject) => {
            axios({
                method: 'GET',
                url: '/api/orders/current',
                headers: {
                    'Accept': 'application/json'
                }
            })
            .then(response => {
                this.order = response.data;
                this.fetched = true;
                this.fetching = false;
                resolve(response.data);
            })
            .catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Set the delivery option on the order
     * @param {string} option
     */
    setDeliveryOption(option, preference) {
        return new Promise((resolve, reject) => {
            axios({
                method: 'PUT',
                url: '/api/orders/delivery/option',
                headers: {
                    'Accept': 'application/json'
                },
                data: {
                    option: option,
                    preference: preference
                }
            }).then(response => {
                resolve(response);
            }).catch(errors => {
                reject(errors.response.data);
            });
        });
    },

    /**
     * Get the shipping order line
     */
    getShippingLine() {
        var shipping = _.find(this.order.lines, line => {
            return line.is_shipping && !line.is_manual && (line.sku != 'long_lengths_delivery');
        });
        return shipping ? shipping : null;
    },

    /**
     * Get the shipping order line
     */
    getShippingLines() {
        return _.filter(this.order.lines, line => {
            return line.is_shipping;
        });
    },

    /**
     * Removes a line from an order
     * @param {string} lineId
     */
    removeLine(lineId) {
        return new Promise((resolve, reject) => {
            axios({
                method: 'DELETE',
                url: '/api/orders/lines/' + lineId,
                headers: {
                    'Accept': 'application/json'
                }
            }).then(response => {
                this.refresh();
                resolve(response);
            }).catch(errors => {
                reject(errors.response.data);
            });
        });
    },

    /**
     * Add an order line
     * @param {string} description
     * @param {number} unitPrice
     * @param {number} quantity
     * @param {number} taxRate
     */
    addLine(sku, description, unitPrice, quantity, taxRate, shipping, manual = true) {
        return new Promise((resolve, reject) => {
            axios({
                method: 'POST',
                url: '/api/orders/lines',
                headers: {
                    'Accept': 'application/json'
                },
                data: {
                    sku: sku,
                    description: description,
                    unit_price: unitPrice,
                    quantity: quantity,
                    tax_rate: taxRate,
                    shipping: shipping,
                    manual: manual,
                }
            }).then(response => {
                this.order = response.data;
                resolve(response);
            }).catch(errors => {
                reject(errors.response.data);
            });
        });
    },

    /**
     * Set the contact details on the order
     * @param {string} email
     * @param {string} phone
     */
    setContact(email, phone) {
        return new Promise((resolve, reject) => {
            axios({
                method: 'PUT',
                url: '/api/orders/contact',
                headers: {
                    'Accept': 'application/json'
                },
                data: {
                    email: email,
                    phone: phone
                }
            }).then(response => {
                this.order = response.data;
                resolve(response);
            }).catch(errors => {
                reject(errors.response.data);
            });
        });
    },

    /**
     * Gets all manual lines for the order
     */
    getManualLines() {
        return _.filter(this.order.lines, line => {
            return line.is_manual;
        });
    },


    /**
     * Gets all manual lines for the order
     */
    getDiscountLines() {
        return _.filter(this.order.lines, line => {
            return line.discount_total > 0;
        });
    },

    /**
     * Set the reference on the order
     * @param {string} reference
     */
    setReference(reference) {
        this.reference = reference;
    },

    /**
     * Set the order notes
     * @param {string} notes
     */
    setNotes(notes) {
        this.notes = notes;
    },

    /**
     * Get the estimated delivery choices for an amount and post code
     * @param {string} zip
     * @param {string} amount
     */
    getEstimatedShipping(zip, amount) {
        return new Promise((resolve, reject) => {
            axios({
                method: 'GET',
                url: '/api/orders/shipping/estimate',
                headers: {
                    'Accept': 'application/json'
                },
                params: {
                    limit: 2,
                    amount: amount,
                    zip: zip ? zip : this.postcode
                }
            }).then(response => {
                resolve(response.data);
            }).catch(errors => {
                reject(errors.response.data);
            });
        });
    }
}



// WEBPACK FOOTER //
// ./resources/assets/js/Services/Order.js